<template>
  <div class="movimentacoes-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" :color="corTema" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">{{ iconeTema }}</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">{{ tituloTela }}</h1>
              <div class="text-caption text-grey">
                {{ descricaoTela }}
              </div>
            </div>
          </div>

          <v-spacer />

          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn color="grey-darken-1" variant="text" @click="voltarMenu" class="text-none mr-2"
              prepend-icon="mdi-arrow-left">
              Voltar
            </v-btn>

            <v-btn color="indigo-darken-2" variant="tonal" @click="abrirPainelSapDownloads"
              prepend-icon="mdi-download-box" class="mr-2">
              Painel Downloads
            </v-btn>

            <v-btn color="blue-darken-2" variant="tonal" @click="abrirDialogArquivosExcel" prepend-icon="mdi-file-excel"
              class="mr-2">
              Arquivos Excel
            </v-btn>

            <v-btn v-if="movimentacoesSelecionadas.length > 0" color="success" variant="flat"
              @click="validarEGerarExcel" prepend-icon="mdi-microsoft-excel" class="mr-2" :loading="loadingExcel"
              :disabled="!podeGerarExcel">
              Gerar Excel ({{ movimentacoesSelecionadas.length }})
            </v-btn>

            <v-btn :color="corTema" variant="flat" @click="abrirDialogNovo" prepend-icon="mdi-plus">
              {{ tipoMovimentacao === 'ENTRADA' ? 'Nova Entrada' : 'Nova Saida' }}
            </v-btn>
          </div>
        </div>

        <!-- Tabs ENTRADA / SAIDA -->
        <div class="d-flex align-center mt-3">
          <v-btn-toggle v-model="tipoMovimentacao" mandatory color="primary" variant="outlined" density="compact">
            <v-btn value="ENTRADA" prepend-icon="mdi-package-down" class="text-none">
              Entrada
            </v-btn>
            <v-btn value="SAIDA" prepend-icon="mdi-package-up" class="text-none">
              Saida
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>
    </div>

    <!-- ================= FILTROS EXPANSOVEIS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>

            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ pagination.total }} Movimentacoes
              </v-chip>

              <v-chip v-if="hasActiveFilters" color="warning" variant="tonal" size="small">
                {{ activeFiltersCount }} filtro(s)
              </v-chip>
            </div>
          </div>

          <div class="d-flex align-center gap-2 mr-2">
            <v-btn color="white" variant="flat" size="small" @click="fetchData" :loading="loading">
              <v-icon size="18" color="grey-darken-2">mdi-refresh</v-icon>
              <span class="ml-2 text-grey-darken-2">Atualizar</span>
            </v-btn>

            <v-btn class="ml-5" color="white" variant="flat" size="small" @click="limparFiltros"
              :disabled="!hasActiveFilters">
              <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>

          <v-btn icon variant="text" size="small" color="white" @click="toggleFilters">
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select v-model="filtros.status" :items="statusOptions" label="Status" placeholder="Todos os status"
                  prepend-inner-icon="mdi-flag" variant="outlined" clearable hide-details density="compact" />
              </v-col>

              <v-col cols="12" md="3">
                <v-select v-model="filtros.deposito" :items="depositos" item-title="nome_deposito"
                  item-value="id_deposito"
                  :label="tipoMovimentacao === 'ENTRADA' ? 'Deposito Destino' : 'Deposito Origem'"
                  placeholder="Selecione..." prepend-inner-icon="mdi-warehouse" variant="outlined" clearable
                  hide-details density="compact" />
              </v-col>

              <v-col cols="12" md="3">
                <v-select v-model="filtros.arquivo_gerado" :items="[
                  { title: 'Todos', value: null },
                  { title: 'Nao Gerados', value: 'nao' },
                  { title: 'Gerados', value: 'sim' }
                ]" label="Arquivo Excel" placeholder="Todos" prepend-inner-icon="mdi-file-excel" variant="outlined"
                  clearable hide-details density="compact" />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field v-model="filtros.data_inicio" label="Data Inicio" type="date"
                  prepend-inner-icon="mdi-calendar" variant="outlined" clearable hide-details density="compact" />
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="3">
                <v-text-field v-model="filtros.data_fim" label="Data Fim" type="date" prepend-inner-icon="mdi-calendar"
                  variant="outlined" clearable hide-details density="compact" />
              </v-col>

              <v-col cols="12" md="6"></v-col>

              <v-col cols="12" md="3" class="d-flex justify-end align-center">
                <v-btn :color="corTema" variant="flat" @click="fetchData" :loading="loading">
                  <v-icon start>mdi-filter</v-icon>
                  Filtrar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= GRID / TABELA ================= -->
    <v-card class="table-card" elevation="2">
      <v-data-table-server v-model="movimentacoesSelecionadas" :headers="headers" :items="movimentacoes"
        :items-length="pagination.total" :loading="loading" :item-selectable="itemSelecionavel" item-key="id"
        class="scrollable-table movimentacoes-grid-table" density="compact" fixed-header height="calc(100vh - 480px)"
        show-select return-object>
        <!-- ID -->
        <template #item.id="{ item }">
          <v-chip color="grey-darken-1" variant="tonal" size="small">
            #{{ item.id }}
          </v-chip>
        </template>

        <!-- Item Codigo -->
        <template #item.item_codigo="{ item }">
          <v-chip color="blue-grey" variant="tonal" size="small">
            {{ item.item_codigo }}
          </v-chip>
        </template>

        <!-- Descricao -->
        <template #item.item_descricao="{ item }">
          <v-chip color="blue-grey-lighten-5" variant="flat" size="small" class="descricao-chip">
            <span class="descricao-chip-text">{{ item.item_descricao || '-' }}</span>
            <v-tooltip activator="parent" location="top">
              {{ item.item_descricao || '-' }}
            </v-tooltip>
          </v-chip>
        </template>

        <!-- Quantidade -->
        <template #item.quantidade="{ item }">
          <div class="d-flex justify-end">
            <v-chip color="grey-lighten-4" variant="flat" size="small" class="font-weight-bold quantidade-chip">
              {{ formatarNumero(item.quantidade) }}
            </v-chip>
          </div>
        </template>

        <!-- Deposito -->
        <template #item.deposito="{ item }">
          <v-chip :color="tipoMovimentacao === 'ENTRADA' ? 'teal' : 'blue'" variant="tonal" size="small">
            {{ item.deposito || 'N/A' }} - {{ getNomeDeposito(item.deposito) }}
          </v-chip>
        </template>

        <!-- Localizacao -->
        <template #item.posicao="{ item }">
          <v-chip color="indigo" variant="tonal" size="x-small" label>
            <v-icon start size="14" class="mr-1">mdi-map-marker</v-icon>
            {{ item.posicao }}
          </v-chip>
        </template>

        <!-- Status -->
        <template #item.status_movimentacao="{ item }">
          <v-chip :color="getStatusColor(item.status_movimentacao)" variant="tonal" size="small">
            {{ item.status_movimentacao }}
          </v-chip>
        </template>

        <!-- Data de Geracao do Arquivo -->
        <template #item.data_hora_geracao_arquivo="{ item }">
          <span v-if="item.data_hora_geracao_arquivo" class="text-caption">
            {{ formatarDataHora(item.data_hora_geracao_arquivo) }}
          </span>
          <span v-else class="text-caption text-grey">-</span>
        </template>

        <!-- Arquivo Gerado -->
        <template #item.arquivo_exportado="{ item }">
          <div v-if="item.arquivo_exportado">
            <v-chip color="success" variant="tonal" size="small">
              <v-icon start size="small">mdi-file-excel</v-icon>
              {{ item.arquivo_exportado }}
            </v-chip>
          </div>
          <span v-else class="text-caption text-grey">Nao gerado</span>
        </template>

        <!-- Usuario -->
        <template #item.usuario="{ item }">
          <span class="text-caption">{{ item.usuario || 'N/A' }}</span>
        </template>

        <!-- Acoes -->
        <template #item.acoes="{ item }">
          <div class="d-flex gap-1">
            <v-btn icon size="x-small" variant="text" color="blue" @click="visualizarMovimentacao(item)">
              <v-icon size="18">mdi-eye</v-icon>
              <v-tooltip activator="parent">Visualizar</v-tooltip>
            </v-btn>

            <v-btn v-if="item.status_movimentacao === 'ERRO'" icon size="x-small" variant="text" color="error"
              @click="verErro(item)">
              <v-icon size="18">mdi-alert-circle</v-icon>
              <v-tooltip activator="parent">Ver Erro</v-tooltip>
            </v-btn>

            <v-btn v-if="item.status_movimentacao === 'PENDENTE'" icon size="x-small" variant="text" color="error"
              @click="excluirMovimentacao(item)" :disabled="!!item.arquivo_exportado">
              <v-icon size="18">mdi-delete</v-icon>
              <v-tooltip activator="parent">
                {{ item.arquivo_exportado ? 'Bloqueado - Faz parte de arquivo Excel' : 'Excluir' }}
              </v-tooltip>
            </v-btn>

            <v-icon v-if="item.arquivo_exportado" size="18" color="orange" class="ml-1">
              mdi-file-excel-box
              <v-tooltip activator="parent">
                Faz parte do arquivo: {{ item.arquivo_exportado }}
              </v-tooltip>
            </v-icon>
          </div>
        </template>

        <!-- Nenhum resultado -->
        <template #no-data>
          <div class="text-center pa-6 text-grey">
            Nenhuma Movimentacao encontrada. Ajuste os filtros e clique em "Filtrar".
          </div>
        </template>
      </v-data-table-server>
    </v-card>

    <!-- ================= DIALOG NOVA MOVIMENTACAO ================= -->
    <v-dialog v-model="dialogForm" max-width="1250" scrollable persistent class="premium-dialog">
      <v-card class="rounded-xl overflow-hidden shadow-2xl">
        <!-- Overlay de Carregamento Global do Formulario -->
        <v-overlay v-model="loadingProdutos" contained persistent class="align-center justify-center" scrim="white"
          opacity="0.9" style="z-index: 10;">
          <div class="text-center">
            <v-progress-circular indeterminate color="primary" size="84" width="8"></v-progress-circular>
            <div class="mt-4 text-h6 font-weight-bold text-primary">Sincronizando com SAP...</div>
            <div class="text-body-2 text-grey">Carregando estoque e enderecamentos completos</div>
          </div>
        </v-overlay>
        <v-card-title :class="`bg-${corTema} text-white d-flex align-center text-h6 font-weight-bold`">
          <v-icon class="mr-2">{{ iconeTema }}</v-icon>
          {{ tipoMovimentacao === 'ENTRADA' ? 'Nova Entrada de Estoque' : 'Nova Saida de Estoque' }}
          <v-spacer />
          <span class="text-caption">ref# dg-me-form</span>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-row no-gutters>
            <!-- COLUNA ESQUERDA: FORMULARIO -->
            <v-col cols="12" :md="produtoSelecionado ? 5 : 12" class="pa-4 border-e">
              <div class="text-subtitle-1 font-weight-bold mb-4 d-flex align-center">
                <v-icon size="20" class="mr-2" :color="corTema">mdi-form-select</v-icon>
                Dados da Movimentacao
              </div>

              <v-form ref="formMovimentacao" @submit.prevent>
                <v-row dense>
                  <v-col cols="12" md="10">
                    <v-text-field v-model="form.item_codigo" label="Codigo do Produto *"
                      prepend-inner-icon="mdi-barcode" variant="outlined" density="compact"
                      :rules="[v => !!v || 'Campo obrigatorio']" @blur="buscarProdutoPorCodigo"
                      @keyup.enter.prevent="buscarProdutoPorCodigo" :loading="loadingProdutos" :readonly="!!produtoSelecionado"
                      placeholder="E0055..." hint="Enter para buscar" persistent-hint>
                      <template v-if="produtoSelecionado" #append-inner>
                        <v-icon color="success">mdi-lock-check</v-icon>
                      </template>
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="2">
                    <v-btn type="button" :color="corTema" variant="flat" block @click="abrirDialogConsultaProdutos" height="40"
                      :disabled="!!produtoSelecionado">
                      <v-icon>{{ produtoSelecionado ? 'mdi-check-bold' : 'mdi-magnify' }}</v-icon>
                    </v-btn>
                  </v-col>

                  <template v-if="produtoSelecionado">
                  <v-col cols="12">
                    <v-select v-model="form.deposito"
                      :items="tipoMovimentacao === 'ENTRADA' ? depositos : depositosComEstoqueMapa"
                      item-title="nome_deposito" item-value="id_deposito" label="Deposito *"
                      prepend-inner-icon="mdi-warehouse" variant="outlined" density="compact"
                      :rules="[v => !!v || 'Campo obrigatorio']" @update:model-value="onDepositoChange"
                      ref="inputDeposito">
                      <template #item="{ props, item }">
                        <v-list-item v-bind="props">
                          <template #append>
                            <v-chip color="primary" variant="tonal" size="small" class="font-weight-bold">
                              Saldo {{ formatarNumero(saldoDepositoOpcao(item.raw)) }}
                            </v-chip>
                          </template>
                        </v-list-item>
                      </template>
                      <template #selection="{ item }">
                        <span>
                          {{ item.raw?.nome_deposito || '' }}
                          <span class="text-primary font-weight-bold ml-1">
                            (Saldo {{ formatarNumero(saldoDepositoOpcao(item.raw)) }})
                          </span>
                        </span>
                      </template>
                    </v-select>
                  </v-col>

                  <v-col v-if="depositoSelecionadoSomenteGestor" cols="12">
                    <v-alert color="warning" variant="tonal" density="compact" icon="mdi-database-alert">
                      Este deposito ainda nao tem saldo no SAP para este produto. Saldo exibido vem do Gestor/Calculado.
                    </v-alert>
                  </v-col>

                  <template v-if="form.deposito">
                    <v-col cols="12" md="6">
                      <v-select v-model="form.rua" :items="ruasDisponiveis" label="Rua" variant="outlined"
                        density="compact" item-title="title" item-value="value" :rules="[validarRua]"
                        clearable @update:model-value="onRuaChange" />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select v-model="form.prateleira" :items="prateleirasDisponiveis" label="Prat."
                        variant="outlined" density="compact" item-title="title" item-value="value" :rules="[validarPrateleira]"
                        clearable @update:model-value="onPrateleiraChange"
                        :disabled="!form.rua" />
                    </v-col>

                    <v-col cols="12">
                      <v-autocomplete v-model="form.posicao" :items="enderecosSapFiltrados" item-title="display"
                        item-value="codigo_localizacao" :label="localizacaoObrigatoria ? 'Localizacao / Bin Code *' : 'Localizacao / Bin Code'" variant="outlined"
                        density="compact" :rules="[validarLocalizacao]"
                        @update:model-value="onPosicaoChange" :loading="loadingEnderecosSap"
                        :placeholder="localizacaoObrigatoria ? 'Selecione o endereco' : 'Sem endereco obrigatorio neste deposito'"
                        :disabled="!localizacaoObrigatoria && enderecosSapFiltrados.length === 0" clearable>
                        <template #item="{ props, item }">
                          <v-list-item v-bind="props">
                            <template #append>
                              <v-chip color="primary" variant="tonal" size="small" class="font-weight-bold">
                                Saldo {{ formatarNumero(saldoEndereco(item.raw)) }}
                              </v-chip>
                            </template>
                          </v-list-item>
                        </template>
                        <template #selection="{ item }">
                          <span>
                            {{ item.raw?.codigo_localizacao || item.raw?.display || '' }}
                            <span class="text-primary font-weight-bold ml-1">
                              (Saldo {{ formatarNumero(saldoEndereco(item.raw)) }})
                            </span>
                          </span>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="7">
                      <v-text-field v-model.number="form.quantidade" type="number" label="Quantidade *"
                        prepend-inner-icon="mdi-counter" variant="outlined" density="compact" :rules="[
                          v => !!v || 'Campo obrigatorio',
                          v => v > 0 || 'Deve ser maior que zero',
                          v => validarQuantidadeMovimentacao(v)
                        ]" ref="inputQuantidade" />
                    </v-col>

                    <v-col cols="12" md="5">
                      <v-text-field v-model="form.unidade_medida" label="U.M *" readonly variant="outlined"
                        density="compact" />
                    </v-col>

                    <v-col cols="12">
                      <v-textarea v-model="form.observacao" label="Observacoes" variant="outlined"
                        density="compact" rows="3" class="observacao-scroll" prepend-inner-icon="mdi-comment-text-outline"
                        placeholder="Motivo da Movimentacao, avaria, etc..." />
                    </v-col>
                  </template>
                  </template>
                </v-row>
              </v-form>
            </v-col>

            <!-- COLUNA DIREITA: ESTOQUE REAL-TIME -->
            <v-col v-if="produtoSelecionado" cols="12" md="7" class="pa-4 bg-grey-lighten-4">
              <EstoqueRealtimePanel
                :produto="produtoSelecionado"
                :depositos="estoquesPorDepositoVisiveis"
                :localizacoes="estoquesDetalhados"
                :loading="loadingEstoquesLocalizacao"
                show-select
                :selected-deposito="form.deposito"
                :select-label="tipoMovimentacao === 'ENTRADA' ? 'Entrada' : 'Saida'"
                @selecionar-deposito="selecionarDepositoMapa"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn type="button" color="grey" variant="text" @click="fecharDialogForm">
            Cancelar
          </v-btn>
          <v-btn type="button" :color="corTema" variant="flat" @click="salvarMovimentacao" :loading="salvando">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG VISUALIZAR ================= -->
    <v-dialog v-model="dialogVisualizar" max-width="760" scrollable>
      <v-card class="detalhes-dialog-card">
        <v-card-title class="bg-blue text-white d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-eye</v-icon>
            Detalhes da Movimentacao #{{ movimentacaoSelecionada.id }}
          </div>
          <div v-if="movimentacaoSelecionada" class="detalhes-ref-tag">
            {{ getReferenciaMovimentacao(movimentacaoSelecionada) }}
          </div>
        </v-card-title>

        <v-card-text class="pa-4 detalhes-dialog-body">
          <v-row v-if="movimentacaoSelecionada" dense>
            <v-col cols="12" md="6">
              <strong>Tipo:</strong>
              <v-chip :color="movimentacaoSelecionada.tipo_movimentacao === 'ENTRADA' ? 'green' : 'red'" variant="tonal"
                size="small" class="ml-2">
                {{ movimentacaoSelecionada.tipo_movimentacao }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <strong>Status:</strong>
              <v-chip :color="getStatusColor(movimentacaoSelecionada.status_movimentacao)" variant="tonal" size="small"
                class="ml-2">
                {{ movimentacaoSelecionada.status_movimentacao }}
              </v-chip>
            </v-col>
            <v-col cols="12" md="6">
              <strong>Codigo Item:</strong> {{ movimentacaoSelecionada.item_codigo }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Quantidade:</strong> {{ formatarNumero(movimentacaoSelecionada.quantidade) }}
            </v-col>
            <v-col cols="12">
              <strong>Descricao:</strong> {{ movimentacaoSelecionada.item_descricao }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Deposito:</strong> {{ movimentacaoSelecionada.deposito }} - {{
                getNomeDeposito(movimentacaoSelecionada.deposito) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Posicao:</strong> {{ movimentacaoSelecionada.posicao || 'N/A' }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Unidade Medida:</strong> {{ movimentacaoSelecionada.unidade_medida }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Usuario:</strong> {{ movimentacaoSelecionada.usuario || 'N/A' }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Data Cadastro:</strong> {{ formatarDataHora(movimentacaoSelecionada.created_at) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Referencia:</strong> {{ getReferenciaMovimentacao(movimentacaoSelecionada) }}
            </v-col>
            <v-col cols="12" v-if="movimentacaoSelecionada.observacao">
              <strong>Observacoes:</strong>
              <p class="mt-1 mb-0">{{ movimentacaoSelecionada.observacao }}</p>
            </v-col>
            <v-col cols="12" md="6" v-if="movimentacaoSelecionada.arquivo_exportado">
              <strong>Arquivo Excel:</strong> {{ movimentacaoSelecionada.arquivo_exportado }}
            </v-col>
            <v-col cols="12" v-if="movimentacaoSelecionada.mensagem_erro">
              <strong>Erro:</strong>
              <p class="mt-1 text-error">{{ movimentacaoSelecionada.mensagem_erro }}</p>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogVisualizar = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG CONFIRMACAO ================= -->
    <v-dialog v-model="dialogConfirmacao" max-width="500">
      <v-card>
        <v-card-title class="bg-warning text-white d-flex align-center">
          <v-icon class="mr-2">mdi-alert</v-icon>
          {{ tituloConfirmacao }}
        </v-card-title>

        <v-card-text class="pa-4">
          <p>{{ mensagemConfirmacao }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogConfirmacao = false">Cancelar</v-btn>
          <v-btn :color="corBotaoConfirmacao" variant="flat" @click="executarAcao">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG CONSULTA PRODUTOS ================= -->
    <v-dialog v-model="dialogConsultaProdutos" max-width="1200" scrollable>
      <v-card>
        <v-card-title :class="`bg-${corTema} text-white d-flex align-center`">
          <v-icon class="mr-2">mdi-package-variant</v-icon>
          Consultar Produtos
          <v-spacer />
          <span class="text-caption">ref# dg-me-cons</span>
          <v-spacer />
          <v-btn icon variant="text" @click="dialogConsultaProdutos = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-row dense class="mb-4">
            <v-col cols="12" md="6">
              <v-text-field v-model="consultaProdutos.filtros.codigo" label="Codigo do Produto"
                prepend-inner-icon="mdi-barcode" variant="outlined" density="compact" clearable
                @keyup.enter="buscarProdutosDialog" />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field v-model="consultaProdutos.filtros.nome" label="Nome do Produto"
                prepend-inner-icon="mdi-text" variant="outlined" density="compact" clearable
                @keyup.enter="buscarProdutosDialog" />
            </v-col>
            <v-col cols="12">
              <v-btn :color="corTema" variant="flat" @click="buscarProdutosDialog" :loading="consultaProdutos.loading">
                <v-icon start>mdi-magnify</v-icon>
                Buscar
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table :headers="[
            { title: 'Codigo', key: 'codigo_produto', width: '120px' },
            { title: 'Nome', key: 'nome_produto' },
            { title: 'Tamanho', key: 'tamanho', width: '100px' },
            { title: 'Estoque', key: 'estoque_fisico', width: '100px', align: 'end' },
            { title: 'Comprom.', key: 'comprometido', width: '100px', align: 'end' },
            { title: 'Disp.', key: 'disponivel_previsto', width: '100px', align: 'end' },
            { title: 'Gestor', key: 'saldo_gestor_pendente', width: '100px', align: 'end' },
            { title: 'Calculado', key: 'saldo_projetado', width: '110px', align: 'end' },
            { title: 'Acoes', key: 'acoes', width: '120px', sortable: false },
          ]" :items="consultaProdutos.produtos" :loading="consultaProdutos.loading" density="compact"
            :items-per-page="10" class="elevation-1">
            <template #item.codigo_produto="{ item }">
              <v-chip color="blue-darken-1" variant="tonal" size="small">
                {{ item.codigo_produto }}
              </v-chip>
            </template>

            <template #item.estoque_fisico="{ item }">
              <v-btn variant="text" size="small"
                :class="saldoCalculadoItem(item, 'produto') > 0 ? 'text-success font-weight-bold' : 'text-grey'"
                @click="exibirSaldosNoDialog(item)" class="pa-0" :disabled="saldoCalculadoItem(item, 'produto') <= 0">
                {{ formatarNumero(item.estoque_fisico) }}
                <v-icon v-if="saldoCalculadoItem(item, 'produto') > 0" size="14" class="ml-1">mdi-eye</v-icon>
              </v-btn>
            </template>

            <template #item.comprometido="{ item }">
              <span class="text-warning">{{ formatarNumero(item.comprometido) }}</span>
            </template>

            <template #item.disponivel_previsto="{ item }">
              <span class="text-primary font-weight-bold">
                {{ formatarNumero(item.disponivel_previsto) }}
              </span>
            </template>

            <template #item.saldo_gestor_pendente="{ item }">
              <span class="font-weight-bold" :class="saldoGestorItem(item) >= 0 ? 'text-success' : 'text-error'">
                {{ formatarNumeroComSinal(saldoGestorItem(item)) }}
              </span>
            </template>

            <template #item.saldo_projetado="{ item }">
              <span class="text-primary font-weight-black">
                {{ formatarNumero(saldoCalculadoItem(item, 'produto')) }}
              </span>
            </template>

            <template #item.acoes="{ item }">
              <v-btn :color="corTema" variant="flat" size="small" @click="selecionarProdutoDialog(item)">
                <v-icon start size="small">mdi-check</v-icon>
                Selecionar
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG AUXILIAR: ESTOQUE POR DEPOSITO (DENTRO DA CONSULTA) ================= -->
    <v-dialog v-model="dialogSearchEstoque" max-width="1080" scrollable>
      <v-card>
        <v-card-title class="bg-blue-darken-3 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-warehouse</v-icon>
          Estoque por Deposito
          <v-spacer />
          <span class="text-caption">ref# dg-me-est</span>
        </v-card-title>
        <v-card-subtitle class="bg-blue-darken-3 text-white pb-3 text-no-wrap overflow-hidden text-truncate">
          {{ produtoSearchSelecionado.codigo_produto }} - {{ produtoSearchSelecionado.nome_produto }}
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <v-data-table :headers="[
            { title: 'Deposito', key: 'nome_deposito', width: '320px' },
            { title: 'SAP-FISICO', key: 'estoque_fisico', align: 'end' },
            { title: 'Comprometido', key: 'estoque_comprometido', align: 'end' },
            { title: 'Disponivel', key: 'estoque_disponivel', align: 'end' },
            { title: 'Gestor', key: 'saldo_gestor_pendente', align: 'end' },
            { title: 'Calculado', key: 'saldo_projetado', align: 'end' },
          ]" :items="estoquesSearchDeposito" :loading="loadingSearchEstoque" density="compact" class="elevation-0"
            hide-default-footer>
            <template #item.nome_deposito="{ item }">
              <div class="d-flex align-center deposito-cell">
                <span class="text-truncate">{{ item.nome_deposito || `Deposito ${item.codigo_deposito || item.deposito || '-'}` }}</span>
                <v-chip v-if="isSomenteGestor(item)" color="warning" variant="tonal" size="x-small" class="ml-2">
                  Somente Gestor
                </v-chip>
              </div>
            </template>
            <template #item.estoque_fisico="{ item }">
              <v-btn variant="text" size="small" color="success" class="font-weight-bold pa-0"
                @click="exibirLocalizacoesNoDialog(item)" :disabled="saldoCalculadoItem(item, 'deposito') <= 0">
                {{ formatarNumero(item.estoque_fisico) }}
                <v-icon v-if="saldoCalculadoItem(item, 'deposito') > 0" size="14" class="ml-1">mdi-magnify</v-icon>
              </v-btn>
            </template>
            <template #item.estoque_comprometido="{ item }">
              {{ formatarNumero(item.estoque_comprometido) }}
            </template>
            <template #item.estoque_disponivel="{ item }">
              <span class="font-weight-bold">{{ formatarNumero(item.estoque_disponivel) }}</span>
            </template>
            <template #item.saldo_gestor_pendente="{ item }">
              <span class="font-weight-bold" :class="saldoGestorItem(item) >= 0 ? 'text-success' : 'text-error'">
                {{ formatarNumeroComSinal(saldoGestorItem(item)) }}
              </span>
            </template>
            <template #item.saldo_projetado="{ item }">
              <span class="text-primary font-weight-black">
                {{ formatarNumero(saldoCalculadoItem(item, 'deposito')) }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogSearchEstoque = false">Voltar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG AUXILIAR: SALDOS POR LOCALIZACAO (DENTRO DA CONSULTA) ================= -->
    <v-dialog v-model="dialogSearchLocalizacao" max-width="850" scrollable>
      <v-card>
        <v-card-title class="bg-teal-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-map-marker-outline</v-icon>
          Saldos por Localizacao
          <v-spacer />
          <span class="text-caption">ref# dg-me-loc</span>
        </v-card-title>
        <v-card-subtitle class="bg-teal-darken-2 text-white pb-3">
          <strong>{{ depositoSearchSelecionado.nome_deposito }}</strong> - {{ produtoSearchSelecionado.nome_produto }}
        </v-card-subtitle>
        <v-card-text class="pa-0">
          <v-data-table :headers="[
            { title: 'Localizacao', key: 'codigo_localizacao' },
            { title: 'Rua', key: 'rua' },
            { title: 'Prat.', key: 'prateleira' },
            { title: 'SAP-FISICO', key: 'estoque_localizacao', align: 'end' },
            { title: 'Gestor', key: 'saldo_gestor_pendente', align: 'end' },
            { title: 'Calculado', key: 'saldo_projetado', align: 'end' },
          ]" :items="estoquesSearchLocalizacao" :loading="loadingSearchLocalizacao" density="compact"
            hide-default-footer>
            <template #item.codigo_localizacao="{ item }">
              {{ item.codigo_localizacao || 'Sem localizacao' }}
            </template>
            <template #item.estoque_localizacao="{ item }">
              <span class="text-success font-weight-bold">{{ formatarNumero(item.estoque_localizacao) }}</span>
            </template>
            <template #item.saldo_gestor_pendente="{ item }">
              <span class="font-weight-bold" :class="saldoGestorItem(item) >= 0 ? 'text-success' : 'text-error'">
                {{ formatarNumeroComSinal(saldoGestorItem(item)) }}
              </span>
            </template>
            <template #item.saldo_projetado="{ item }">
              <span class="text-primary font-weight-black">
                {{ formatarNumero(saldoCalculadoItem(item, 'localizacao')) }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" variant="text" @click="dialogSearchLocalizacao = false">Voltar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG PESQUISANDO AGUARDE ================= -->
    <v-dialog v-model="loadingProdutos" max-width="350" persistent>
      <v-card class="pa-4 text-center rounded-xl overflow-hidden">
        <v-card-text class="pt-6">
          <v-progress-circular indeterminate color="primary" size="84" width="8" class="mb-6">
            <v-icon color="primary" size="40">mdi-barcode-scan</v-icon>
          </v-progress-circular>
          <div class="text-h6 font-weight-bold mb-2">Buscando no SAP...</div>
          <div class="text-body-2 text-grey-darken-1 mb-4">
            Aguarde enquanto consultamos os estoques e localizacoes do produto.
          </div>
          <v-progress-linear indeterminate color="primary" height="2" rounded />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG ARQUIVOS EXCEL ================= -->
    <v-dialog v-model="dialogArquivosExcel" max-width="900" scrollable>
      <v-card>
        <v-card-title class="bg-blue-darken-2 text-white d-flex align-center">
          <v-icon class="mr-2">mdi-file-excel</v-icon>
          Arquivos Excel Gerados
          <v-spacer />
          <v-btn icon variant="text" @click="dialogArquivosExcel = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-data-table :headers="[
            { title: 'Arquivo', key: 'arquivo_exportado' },
            { title: 'Tipo', key: 'tipo_movimentacao', width: '100px' },
            { title: 'Data Geracao', key: 'data_hora_geracao_arquivo', width: '180px' },
            { title: 'Qtd. Movimentacoes', key: 'quantidade_movimentacoes', width: '150px' },
            { title: 'Acoes', key: 'acoes', width: '200px', sortable: false }
          ]" :items="arquivosExcel" :loading="loadingArquivosExcel" density="compact" :items-per-page="10"
            class="elevation-1">
            <template #item.arquivo_exportado="{ item }">
              <v-chip color="green-darken-1" variant="tonal" size="small">
                <v-icon start size="small">mdi-file-excel</v-icon>
                {{ item.arquivo_exportado }}
              </v-chip>
            </template>

            <template #item.tipo_movimentacao="{ item }">
              <v-chip :color="item.tipo_movimentacao === 'ENTRADA' ? 'green' : 'red'" variant="tonal" size="x-small">
                {{ item.tipo_movimentacao }}
              </v-chip>
            </template>

            <template #item.data_hora_geracao_arquivo="{ item }">
              <span class="text-caption">{{ formatarDataHora(item.data_hora_geracao_arquivo) }}</span>
            </template>

            <template #item.acoes="{ item }">
              <div class="d-flex gap-1">
                <v-btn color="success" variant="tonal" size="small" @click="baixarArquivoExcel(item.arquivo_exportado)">
                  <v-icon start size="small">mdi-download</v-icon>
                  Baixar
                </v-btn>
                <v-btn color="error" variant="tonal" size="small" @click="confirmarCancelarArquivo(item)">
                  <v-icon start size="small">mdi-close-circle</v-icon>
                  Cancelar
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { sapMovimentacaoEstoqueService } from "@/services/sapMovimentacaoEstoqueService";
import { transferenciaEstoqueService } from "@/services/transferenciaEstoqueService";
import { sapeJodarcService } from "@/services/sapeJodarcService";
import EstoqueRealtimePanel from "@/components/sape/EstoqueRealtimePanel.vue";
import { useToast } from "vue-toastification";

const toast = useToast();

export default {
  name: "SapMovimentacaoEstoque",
  components: { EstoqueRealtimePanel },

  data() {
    return {
      // Tipo de Movimentacao (ENTRADA ou SAIDA) a vem da query ou default ENTRADA
      tipoMovimentacao: 'ENTRADA',

      // Dados da tabela
      movimentacoes: [],
      movimentacoesSelecionadas: [],
      loading: false,
      pagination: { total: 0 },

      // Filtros
      filtros: {
        status: null,
        deposito: null,
        data_inicio: null,
        data_fim: null,
        arquivo_gerado: null,
      },
      filtersExpanded: false,
      statusOptions: ['PENDENTE', 'PROCESSADA', 'ERRO'],

      // Depositos
      depositos: [],
      depositosComEstoque: [],
      enderecos: [],
      todosEnderecos: [],

      // Dialog Form
      dialogForm: false,
      salvando: false,
      form: {
        item_codigo: '',
        item_descricao: '',
        quantidade: null,
        deposito: '',
        posicao: '',
        rua: '',
        id_rua: null,
        prateleira: '',
        id_prateleira: null,
        unidade_medida: '',
        unidade_medida_estoque: 'S',
        saldo_estoque: null,
        observacao: '',
      },

      // Dados SAP Detalhados
      loadingEstoquesLocalizacao: false,
      estoquesDetalhados: [],
      sapEnderecosDeposito: [],
      loadingEnderecosSap: false,

      // Produto
      produtoSelecionado: null,
      loadingProdutos: false,

      // Dialog Consulta Produtos
      dialogConsultaProdutos: false,
      consultaProdutos: {
        filtros: { codigo: '', nome: '' },
        produtos: [],
        loading: false,
      },

      // Drill-down na consulta
      dialogSearchEstoque: false,
      loadingSearchEstoque: false,
      produtoSearchSelecionado: null,
      estoquesSearchDeposito: [],

      dialogSearchLocalizacao: false,
      loadingSearchLocalizacao: false,
      depositoSearchSelecionado: null,
      estoquesSearchLocalizacao: [],

      // Dialog Visualizar
      dialogVisualizar: false,
      movimentacaoSelecionada: null,

      // Dialog Confirmacao
      dialogConfirmacao: false,
      tituloConfirmacao: '',
      mensagemConfirmacao: '',
      corBotaoConfirmacao: 'error',
      acaoCallback: null,

      // Excel
      loadingExcel: false,
      dialogArquivosExcel: false,
      arquivosExcel: [],
      loadingArquivosExcel: false,
      arquivosRecemGerados: [],

      // Headers da tabela
      headers: [
        { title: 'ID', key: 'id', width: '80px' },
        { title: 'Codigo Item', key: 'item_codigo', width: '130px' },
        { title: 'Descricao', key: 'item_descricao', width: '360px' },
        { title: 'Qtd', key: 'quantidade', width: '110px', align: 'end' },
        { title: 'Deposito', key: 'deposito', width: '170px' },
        { title: 'Localizacao', key: 'posicao', width: '190px' },
        { title: 'Status', key: 'status_movimentacao', width: '120px' },
        { title: 'Arquivo Excel', key: 'arquivo_exportado', width: '160px' },
        { title: 'Usuario', key: 'usuario', width: '140px' },
        { title: 'Acoes', key: 'acoes', width: '120px', sortable: false },
      ],
    };
  },

  computed: {
    corTema() {
      return this.tipoMovimentacao === 'ENTRADA' ? 'green-darken-2' : 'red-darken-2';
    },
    iconeTema() {
      return this.tipoMovimentacao === 'ENTRADA' ? 'mdi-package-down' : 'mdi-package-up';
    },
    tituloTela() {
      return this.tipoMovimentacao === 'ENTRADA' ? 'SAP Entrada de Estoque' : 'SAP Saida de Estoque';
    },
    descricaoTela() {
      return this.tipoMovimentacao === 'ENTRADA'
        ? 'Gerenciar entradas de mercadoria nos depositos'
        : 'Gerenciar sadas de mercadoria dos depositos';
    },
    hasActiveFilters() {
      return Object.values(this.filtros).some(v => v !== null && v !== '');
    },
    activeFiltersCount() {
      return Object.values(this.filtros).filter(v => v !== null && v !== '').length;
    },
    podeGerarExcel() {
      return this.movimentacoesSelecionadas.length > 0 && !this.loadingExcel;
    },
    estoquesPorDepositoAgrupado() {
      // Iniciar mapa com todos os depositos conhecidos
      const mapa = {};
      this.depositos.forEach(d => {
        mapa[d.id_deposito] = {
          codigo_deposito: d.id_deposito,
          nome_deposito: d.nome_deposito,
          estoque_total: 0,
          sap_reservado: 0,
          sap_disponivel: 0,
          entradas_pendentes: 0,
          saidas_pendentes: 0,
          saldo_projetado: 0
        };
      });

      // Somar saldos vindos do SAP (por endereco)
      if (this.estoquesDetalhados && this.estoquesDetalhados.length > 0) {
        this.estoquesDetalhados.forEach(item => {
          const sapCode = String(item.codigo_deposito);

          // Tentar encontrar no mapa (id_deposito ja e string aqui)
          let matchKey = Object.keys(mapa).find(k =>
            k === sapCode ||
            k.padStart(2, '0') === sapCode.padStart(2, '0') ||
            parseInt(k) === parseInt(sapCode)
          );

          if (!matchKey) {
            matchKey = sapCode;
            mapa[matchKey] = {
              codigo_deposito: sapCode,
              nome_deposito: item.nome_deposito,
              estoque_total: 0,
              sap_reservado: 0,
              sap_disponivel: 0,
              entradas_pendentes: 0,
              saidas_pendentes: 0,
              saldo_projetado: 0
            };
          }
          const sapFisico = parseFloat(item.estoque_localizacao || item.quantidade_sap || item.estoque_fisico || item.estoque_disponivel || 0);
          const sapReservado = parseFloat(item.estoque_comprometido || item.comprometido || item.sap_reservado || 0);
          const sapDisponivel = parseFloat(item.estoque_disponivel || item.disponivel || (sapFisico - sapReservado) || 0);

          mapa[matchKey].estoque_total += sapFisico;
          mapa[matchKey].sap_reservado += sapReservado;
          mapa[matchKey].sap_disponivel += sapDisponivel;
          mapa[matchKey].entradas_pendentes += parseFloat(item.entradas_pendentes || 0);
          mapa[matchKey].saidas_pendentes += parseFloat(item.saidas_pendentes || 0);
          mapa[matchKey].saldo_projetado += parseFloat(item.saldo_projetado || 0);
        });
      }

      // Limpeza de dizimas e arredondamento para 4 casas para evitar fantasmas de precisao
      Object.keys(mapa).forEach(k => {
        ['estoque_total', 'sap_reservado', 'sap_disponivel', 'entradas_pendentes', 'saidas_pendentes', 'saldo_projetado'].forEach(field => {
          if (mapa[k][field] < 0.0001 && mapa[k][field] > -0.0001) {
            mapa[k][field] = 0;
          } else {
            mapa[k][field] = Math.round(mapa[k][field] * 10000) / 10000;
          }
        });
      });

      // Ordenar: Primeiro os que tOm saldo, depois por codigo de deposito
      return Object.values(mapa).sort((a, b) => {
        if (b.estoque_total !== a.estoque_total) {
          return b.estoque_total - a.estoque_total;
        }
        return a.codigo_deposito.localeCompare(b.codigo_deposito);
      });
    },
    estoquesPorDepositoVisiveis() {
      return this.estoquesPorDepositoAgrupado.filter(d =>
        d.saldo_projetado > 0 ||
        d.estoque_total > 0 ||
        this.saldoGestorDeposito(d) !== 0
      );
    },
    totalEstoqueGeral() {
      return this.estoquesPorDepositoAgrupado.reduce((acc, curr) => acc + curr.saldo_projetado, 0);
    },
    totalEstoqueSap() {
      return this.estoquesPorDepositoAgrupado.reduce((acc, curr) => acc + curr.estoque_total, 0);
    },
    totalSapReservado() {
      return this.estoquesPorDepositoAgrupado.reduce((acc, curr) => acc + parseFloat(curr.sap_reservado || 0), 0);
    },
    totalSapDisponivel() {
      return this.estoquesPorDepositoAgrupado.reduce((acc, curr) => acc + parseFloat(curr.sap_disponivel || 0), 0);
    },
    totalEstoqueGestor() {
      return this.estoquesPorDepositoAgrupado.reduce((acc, curr) => acc + (parseFloat(curr.entradas_pendentes || 0) - parseFloat(curr.saidas_pendentes || 0)), 0);
    },
    saldoNoDepositoSelecionado() {
      if (!this.form.deposito) return 0;
      const dep = this.estoquesPorDepositoAgrupado.find(d => this.isSameDep(d.codigo_deposito, this.form.deposito));
      return dep ? parseFloat(dep.saldo_projetado || 0) : 0;
    },
    saldoSaidaSelecionado() {
      if (this.form.posicao) {
        return parseFloat(this.saldoEnderecoSelecionado || 0);
      }
      return parseFloat(this.saldoNoDepositoSelecionado || 0);
    },
    labelSaldoSaida() {
      return this.form.posicao ? 'Saldo real da localizacao' : 'Saldo real do deposito';
    },
    depositoSelecionadoSomenteGestor() {
      if (!this.form.deposito) return false;
      const dep = this.estoquesPorDepositoAgrupado.find(d => this.isSameDep(d.codigo_deposito, this.form.deposito));
      return dep ? this.isSomenteGestor(dep) : false;
    },
    depositosComEstoqueMapa() {
      return this.estoquesPorDepositoAgrupado
        .filter(d => parseFloat(d.saldo_projetado || 0) > 0)
        .map(d => ({
          id_deposito: d.codigo_deposito,
          nome_deposito: d.nome_deposito
        }));
    },
    enderecosBaseParaSelecao() {
      if (this.tipoMovimentacao !== 'SAIDA') return this.sapEnderecosDeposito;
      return this.sapEnderecosDeposito.filter(e => this.saldoEndereco(e) > 0);
    },
    saldoEnderecoSelecionado() {
      if (!this.form.posicao) return 0;
      const endereco = this.sapEnderecosDeposito.find(
        e => e.codigo_localizacao === this.form.posicao
      );
      return endereco ? this.saldoEndereco(endereco) : 0;
    },
    ruasDisponiveis() {
      const ruas = this.enderecosBaseParaSelecao
        .filter(e => e.rua && e.prateleira)
        .map(e => e.rua);
      return [...new Set(ruas)].sort().map(rua => ({
        title: `${rua} (Saldo ${this.formatarNumero(this.saldoRua(rua))})`,
        value: rua,
      }));
    },
    prateleirasDisponiveis() {
      if (!this.form.rua) return [];
      const prats = this.enderecosBaseParaSelecao
        .filter(e => e.rua === this.form.rua)
        .map(e => e.prateleira)
        .filter(p => p);
      return [...new Set(prats)].sort().map(prateleira => ({
        title: `${prateleira} (Saldo ${this.formatarNumero(this.saldoPrateleira(this.form.rua, prateleira))})`,
        value: prateleira,
      }));
    },
    enderecosSapFiltrados() {
      let filtered = this.enderecosBaseParaSelecao;
      if (this.form.rua) {
        filtered = filtered.filter(e => e.rua === this.form.rua);
      }
      if (this.form.prateleira) {
        filtered = filtered.filter(e => e.prateleira === this.form.prateleira);
      }
      return filtered.map(e => ({
        ...e,
        display: `${e.codigo_localizacao || 'Sem localizacao'} (${e.rua || '-'}/${e.prateleira || '-'}) - Saldo ${this.formatarNumero(this.saldoEndereco(e))}`
      })).sort((a, b) => (a.codigo_localizacao || "").localeCompare(b.codigo_localizacao || ""));
    },
    localizacaoObrigatoria() {
      if (!this.form.deposito) return false;
      return this.enderecosBaseParaSelecao.some(e => !!e.codigo_localizacao);
    }
  },

  watch: {
    tipoMovimentacao() {
      this.movimentacoesSelecionadas = [];
      this.fetchData();
    },
  },

  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await sapMovimentacaoEstoqueService.listarMovimentacoes({
          tipo_movimentacao: this.tipoMovimentacao,
          ...this.filtros,
        });

        if (response?.success) {
          this.movimentacoes = response.dados || [];
          this.pagination.total = response.total || 0;
        } else {
          this.movimentacoes = [];
          this.pagination.total = 0;
          toast.error(response.message || "Erro ao carregar Movimentacoes");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar Movimentacoes");
      } finally {
        this.loading = false;
      }
    },

    async carregarDepositos() {
      try {
        const response = await transferenciaEstoqueService.buscarDepositos();
        if (response?.success) {
          // Normalizar IDs para String para garantir match perfeito com os codigos do SAP (Ex: 01)
          this.depositos = (response.dados || []).map(d => ({
            ...d,
            id_deposito: String(d.id_deposito || d.id)
          }));
        }
      } catch (error) {
        console.error("Erro ao carregar depositos:", error);
      }
    },

    async carregarEnderecos() {
      try {
        const api = (await import("@/api/api")).default;
        const BASE_URL = import.meta.env.VITE_APP_API_URL;
        const response = await api.get(`${BASE_URL}/depositos-enderecos`);
        if (response.status === 200 && response.data) {
          this.todosEnderecos = response.data.dados || response.data || [];
        }
      } catch (error) {
        console.error("Erro ao carregar enderecos:", error);
      }
    },

    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    limparFiltros() {
      this.filtros = {
        status: null,
        deposito: null,
        data_inicio: null,
        data_fim: null,
        arquivo_gerado: null,
      };
      this.fetchData();
    },

    abrirDialogNovo() {
      this.form = {
        item_codigo: '',
        item_descricao: '',
        quantidade: null,
        deposito: '',
        posicao: '',
        unidade_medida: '',
        unidade_medida_estoque: 'S',
        saldo_estoque: null,
      };
      this.produtoSelecionado = null;
      this.estoquesDetalhados = [];
      this.sapEnderecosDeposito = [];
      this.enderecos = [];
      this.depositosComEstoque = [];
      this.dialogForm = true;
    },

    fecharDialogForm() {
      this.dialogForm = false;
      this.produtoSelecionado = null;
      this.estoquesDetalhados = [];
      this.sapEnderecosDeposito = [];
      this.form = {
        item_codigo: '',
        item_descricao: '',
        quantidade: null,
        deposito: '',
        posicao: '',
        unidade_medida: '',
        rua: '',
        prateleira: '',
        saldo_estoque: 0,
      };
    },

    async buscarProdutoPorCodigo() {
      if (!this.form.item_codigo || this.form.item_codigo.trim() === '') return;

      this.loadingProdutos = true;
      try {
        const response = await sapeJodarcService.sapeProdutos({
          page: 1,
          per_page: 1,
          codigo_produto: this.form.item_codigo,
        });

        const produtos = response.data?.data || response.data || [];
        if (response?.success && produtos.length > 0) {
          this.selecionarProduto(produtos[0]);
        } else {
          toast.warning("Produto nao encontrado. Clique na lupa para buscar.");
          this.produtoSelecionado = null;
        }
      } catch (error) {
        console.error("Erro ao buscar produto:", error);
        toast.error("Erro ao buscar produto");
      } finally {
        this.loadingProdutos = false;
      }
    },

    selecionarProduto(produto) {
      this.produtoSelecionado = produto;
      this.form.item_codigo = produto.codigo_produto;
      this.form.item_descricao = produto.nome_produto;
      this.form.unidade_medida = this.unidadeProduto(produto);
      this.form.saldo_estoque = produto.estoque_fisico || 0;
      this.form.deposito = '';
      this.form.posicao = '';
      this.form.rua = '';
      this.form.prateleira = '';

      this.buscarEstoqueDetalhadoSap(produto.codigo_produto);

      // Focar no deposito, pois ele define o saldo real usado na quantidade.
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.inputDeposito) {
            this.$refs.inputDeposito.focus();
          }
        }, 300);
      });
    },

    async buscarEstoqueDetalhadoSap(codigoProduto) {
      this.loadingEstoquesLocalizacao = true;
      try {
        const response = await sapeJodarcService.sapeProdutosEstoquesLocalizacoes({
          codigo_produto: codigoProduto,
          per_page: 500
        });
        if (response?.success) {
          const rawData = response.data;
          console.log('RAW data:', rawData);
          // A API retorna paginado: { data: [...], total: X }
          this.estoquesDetalhados = Array.isArray(rawData?.data) ? rawData.data : (Array.isArray(rawData) ? rawData : []);
          console.log('estoquesDetalhados[0]:', this.estoquesDetalhados[0]);

          // InteligOncia: se houver saldo em apenas UM deposito, ja seleciona ele automaticamente
          this.$nextTick(() => {
            const depositosComSaldo = this.estoquesPorDepositoAgrupado.filter(d => d.estoque_total > 0);
            if (depositosComSaldo.length === 1) {
              this.selecionarDepositoMapa(depositosComSaldo[0]);
            }
          });
        }
      } catch (error) {
        console.error("Erro ao buscar estoque detalhado SAP:", error);
      } finally {
        this.loadingEstoquesLocalizacao = false;
      }
    },

    selecionarDepositoMapa(dep) {
      this.form.deposito = dep.codigo_deposito;
      this.onDepositoChange(dep.codigo_deposito);
    },

    async onDepositoChange(deposito) {
      this.form.posicao = '';
      this.form.rua = '';
      this.form.prateleira = '';
      this.sapEnderecosDeposito = [];

      if (!deposito) return;

      const codigoParaApi = String(deposito).padStart(2, '0');
      this.loadingEnderecosSap = true;

      try {
        const params = { codigo_deposito: codigoParaApi, per_page: 500 };
        const response = await sapeJodarcService.sapeEnderecoDepositos(params);

        let enderecosApi = [];
        if (response?.success) {
          enderecosApi = Array.isArray(response.data?.data)
            ? response.data?.data
            : (Array.isArray(response.data) ? response.data : []);
        }

        const enderecosDoProduto = this.estoquesDetalhados
          .filter(l => this.isSameDep(l.codigo_deposito, codigoParaApi))
          .map(l => ({
            codigo_deposito: l.codigo_deposito,
            rua: l.rua,
            prateleira: l.prateleira,
            codigo_localizacao: l.codigo_localizacao,
            estoque_localizacao: parseFloat(l.estoque_localizacao || 0),
            estoque_comprometido: parseFloat(l.estoque_comprometido || l.comprometido || 0),
            estoque_disponivel: parseFloat(l.estoque_disponivel || l.disponivel || 0),
            saldo_projetado: parseFloat(l.saldo_projetado || 0),
            entradas_pendentes: parseFloat(l.entradas_pendentes || 0),
            saidas_pendentes: parseFloat(l.saidas_pendentes || 0),
          }));

        const todosEnderecos = enderecosApi.map(e => {
          const saldo = enderecosDoProduto.find(s => s.codigo_localizacao === e.codigo_localizacao);
          return {
            ...e,
            estoque_localizacao: parseFloat(saldo?.estoque_localizacao || 0),
            estoque_comprometido: parseFloat(saldo?.estoque_comprometido || 0),
            estoque_disponivel: parseFloat(saldo?.estoque_disponivel || 0),
            saldo_projetado: parseFloat(saldo?.saldo_projetado || 0),
            entradas_pendentes: parseFloat(saldo?.entradas_pendentes || 0),
            saidas_pendentes: parseFloat(saldo?.saidas_pendentes || 0),
          };
        });

        enderecosDoProduto.forEach(ep => {
          if (!todosEnderecos.find(te => te.codigo_localizacao === ep.codigo_localizacao)) {
            todosEnderecos.push(ep);
          }
        });

        this.sapEnderecosDeposito = todosEnderecos;

        this.$nextTick(() => {
          const saldosNesteDep = this.estoquesDetalhados.filter(l =>
            this.isSameDep(l.codigo_deposito, codigoParaApi) &&
            parseFloat(l.estoque_localizacao || 0) > 0
          );

          if (saldosNesteDep.length === 1) {
            const unico = saldosNesteDep[0];
            this.form.rua = unico.rua;
            this.$nextTick(() => {
              this.form.prateleira = unico.prateleira;
              this.$nextTick(() => {
                this.form.posicao = unico.codigo_localizacao;
                this.onPosicaoChange(this.form.posicao);
              });
            });
          }

          if (this.tipoMovimentacao === 'SAIDA' && this.enderecosBaseParaSelecao.length === 0) {
            toast.warning('Este deposito nao possui enderecos com saldo para saida.');
          }
        });
      } catch (error) {
        console.error('Erro ao buscar enderecos SAP:', error);
        toast.error('Erro ao carregar enderecos do deposito');
      } finally {
        this.loadingEnderecosSap = false;
      }
    },

    onRuaChange() {
      this.form.prateleira = '';
      this.form.posicao = '';
    },

    onPrateleiraChange() {
      this.form.posicao = '';
      // Se tiver apenas uma posicao nessa rua/prateleira, ja seleciona
      const filtrados = this.enderecosSapFiltrados;
      if (filtrados.length === 1) {
        this.form.posicao = filtrados[0].codigo_localizacao;
        this.onPosicaoChange(this.form.posicao);
      }
    },

    onPosicaoChange(codigo) {
      if (codigo) {
        const enc = this.sapEnderecosDeposito.find(e => e.codigo_localizacao === codigo);
        if (enc) {
          this.form.rua = enc.rua;
          this.form.id_rua = enc.id_rua;
          this.form.prateleira = enc.prateleira;
          this.form.id_prateleira = enc.id_prateleira;
        }
      } else {
        this.form.id_rua = null;
        this.form.id_prateleira = null;
      }
    },

    async salvarMovimentacao() {
      const { valid } = await this.$refs.formMovimentacao.validate();
      if (!valid) return;

      this.salvando = true;
      try {
        const dados = {
          tipo_movimentacao: this.tipoMovimentacao,
          item_codigo: this.form.item_codigo,
          item_descricao: this.form.item_descricao,
          quantidade: this.form.quantidade,
          deposito: this.form.deposito,
          posicao: this.form.posicao || null,
          rua: this.form.rua,
          id_rua: this.form.id_rua,
          prateleira: this.form.prateleira,
          id_prateleira: this.form.id_prateleira,
          saldo_estoque: this.form.saldo_estoque,
          unidade_medida: this.form.unidade_medida,
          unidade_medida_estoque: this.form.unidade_medida_estoque,
          observacao: this.form.observacao,
        };

        const response = await sapMovimentacaoEstoqueService.criarMovimentacao(dados);

        if (response?.success) {
          toast.success(response.message || "Movimentacao salva com sucesso");
          await this.buscarEstoqueDetalhadoSap(this.form.item_codigo);
          this.fecharDialogForm();
          await this.fetchData();
        } else {
          toast.error(response.message || "Erro ao salvar Movimentacao");
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao salvar Movimentacao");
      } finally {
        this.salvando = false;
      }
    },

    // ===== DRILL-DOWN NA CONSULTA (NOVO) =====
    async exibirSaldosNoDialog(produto) {
      console.log("Chamando exibirSaldosNoDialog para:", produto.codigo_produto);
      this.produtoSearchSelecionado = produto;
      this.dialogSearchEstoque = true;
      this.loadingSearchEstoque = true;
      this.estoquesSearchDeposito = [];

      try {
        const response = await sapeJodarcService.sapeProdutosEstoquesDepositos({
          codigo_produto: produto.codigo_produto
        });
        if (response?.success) {
          this.estoquesSearchDeposito = Array.isArray(response.data) ? response.data : [];
        }
      } catch (error) {
        console.error("Erro ao carregar estoque deposito:", error);
      } finally {
        this.loadingSearchEstoque = false;
      }
    },

    async exibirLocalizacoesNoDialog(deposito) {
      this.depositoSearchSelecionado = deposito;
      this.dialogSearchLocalizacao = true;
      this.loadingSearchLocalizacao = true;
      this.estoquesSearchLocalizacao = [];

      try {
        const response = await sapeJodarcService.sapeProdutosEstoquesLocalizacoes({
          codigo_produto: this.produtoSearchSelecionado.codigo_produto,
          codigo_deposito: deposito.codigo_deposito
        });
        if (response?.success) {
          const rawData2 = response.data;
          this.estoquesSearchLocalizacao = Array.isArray(rawData2?.data) ? rawData2?.data : (Array.isArray(rawData2) ? rawData2 : []);
        }
      } catch (error) {
        console.error("Erro ao carregar localizacoes:", error);
      } finally {
        this.loadingSearchLocalizacao = false;
      }
    },

    visualizarMovimentacao(item) {
      this.movimentacaoSelecionada = item;
      this.dialogVisualizar = true;
    },

    verErro(item) {
      if (item.mensagem_erro) {
        toast.error(item.mensagem_erro);
      } else {
        toast.info("Nenhuma mensagem de erro disponivel");
      }
    },

    excluirMovimentacao(item) {
      this.movimentacaoSelecionada = item;
      this.tituloConfirmacao = "Confirmar Exclusao";
      this.mensagemConfirmacao = `Deseja realmente excluir a Movimentacao #${item.id}`;
      this.corBotaoConfirmacao = "error";
      this.acaoCallback = async () => {
        try {
          const response = await sapMovimentacaoEstoqueService.excluirMovimentacao(item.id);
          if (response?.success) {
            toast.success("Movimentacao excluida com sucesso");
            this.fetchData();
          } else {
            toast.error(response.message || "Erro ao excluir Movimentacao");
          }
        } catch (error) {
          console.error("Erro ao excluir:", error);
          toast.error("Erro ao excluir Movimentacao");
        }
      };
      this.dialogConfirmacao = true;
    },

    async executarAcao() {
      if (this.acaoCallback) {
        await this.acaoCallback();
        this.dialogConfirmacao = false;
        this.acaoCallback = null;
      }
    },

    // ===== CONSULTA PRODUTOS =====
    abrirDialogConsultaProdutos() {
      this.consultaProdutos.filtros = { codigo: '', nome: '' };
      this.consultaProdutos.produtos = [];
      this.dialogConsultaProdutos = true;
    },

    async buscarProdutosDialog() {
      if (!this.consultaProdutos.filtros.codigo && !this.consultaProdutos.filtros.nome) {
        toast.warning("Digite o codigo ou nome do produto");
        return;
      }

      this.consultaProdutos.loading = true;
      try {
        const params = {
          page: 1,
          per_page: 50,
        };

        Object.assign(params, this.montarFiltrosConsultaProduto());

        const response = await sapeJodarcService.sapeProdutos(params);

        if (response?.success) {
          this.consultaProdutos.produtos = Array.isArray(response.data) ? response.data : [];

          if (this.consultaProdutos.produtos.length === 0) {
            toast.info("Nenhum produto encontrado");
          }
        } else {
          this.consultaProdutos.produtos = [];
          toast.error("Erro ao buscar produtos");
        }
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
        this.consultaProdutos.produtos = [];
        toast.error("Erro ao buscar produtos");
      } finally {
        this.consultaProdutos.loading = false;
      }
    },

    selecionarProdutoDialog(produto) {
      this.selecionarProduto(produto);
      this.dialogConsultaProdutos = false;
    },

    montarFiltrosConsultaProduto() {
      const codigo = String(this.consultaProdutos.filtros.codigo || '').trim();
      const nome = String(this.consultaProdutos.filtros.nome || '').trim();
      const filtros = {};

      if (nome) {
        filtros.nome_produto = nome;
      }

      if (codigo) {
        const pareceDescricao = /[%_\s]/.test(codigo) || !/\d/.test(codigo);
        if (pareceDescricao && !nome) {
          filtros.nome_produto = codigo;
        } else {
          filtros.codigo_produto = codigo;
        }
      }

      return filtros;
    },

    // ===== EXCEL =====
    itemSelecionavel(item) {
      return !item.arquivo_exportado || item.arquivo_exportado.trim() === '';
    },

    validarEGerarExcel() {
      const comArquivo = this.movimentacoesSelecionadas.filter(
        t => t.arquivo_exportado && t.arquivo_exportado.trim() !== ''
      );

      if (comArquivo.length > 0) {
        const arquivos = [...new Set(comArquivo.map(t => t.arquivo_exportado))].join(', ');
        toast.error(`Algumas Movimentacoes ja possuem arquivo Excel gerado: ${arquivos}. Cancele o arquivo primeiro.`);
        return;
      }

      this.gerarExcel();
    },

    async gerarExcel() {
      if (this.movimentacoesSelecionadas.length === 0) {
        toast.warning("Selecione pelo menos uma Movimentacao");
        return;
      }

      this.loadingExcel = true;
      try {
        const movimentacoes = this.movimentacoesSelecionadas.map(t => t.id);
        const tipoLabel = this.tipoMovimentacao === 'ENTRADA' ? 'entradas' : 'saidas';
        const nomeArquivo = `sap_${tipoLabel}_` + new Date().toISOString().replace(/[:.]/g, '_').slice(0, -5) + '.xlsx';

        const response = await sapMovimentacaoEstoqueService.gerarExcel({
          movimentacoes,
          nome_arquivo: nomeArquivo
        });

        if (response?.success) {
          toast.success(response.message || "Excel gerado! Acesse o Painel SAP Downloads para baixar.", {
            timeout: 8000,
            onClick: () => {
              this.$router.push({ name: 'SapDownloads' });
            }
          });

          this.arquivosRecemGerados.push({
            nome: response.data.nome_arquivo || nomeArquivo,
            timestamp: Date.now()
          });

          this.movimentacoesSelecionadas = [];
          this.fetchData();
        } else {
          toast.error(response.message || "Erro ao gerar Excel");
        }
      } catch (error) {
        console.error("Erro ao gerar Excel:", error);
        toast.error("Erro ao gerar Excel");
      } finally {
        this.loadingExcel = false;
      }
    },

    async abrirDialogArquivosExcel() {
      this.dialogArquivosExcel = true;
      await this.listarArquivosExcel();
    },

    async listarArquivosExcel() {
      this.loadingArquivosExcel = true;
      try {
        const response = await sapMovimentacaoEstoqueService.listarArquivosExcel({
          tipo_movimentacao: this.tipoMovimentacao
        });

        if (response?.success) {
          this.arquivosExcel = response.dados || [];
        } else {
          this.arquivosExcel = [];
        }
      } catch (error) {
        console.error("Erro ao listar arquivos Excel:", error);
        this.arquivosExcel = [];
      } finally {
        this.loadingArquivosExcel = false;
      }
    },

    async baixarArquivoExcel(nomeArquivo) {
      try {
        const response = await sapMovimentacaoEstoqueService.baixarExcel(nomeArquivo);
        if (response?.success) {
          toast.success("Download iniciado!");
        } else {
          toast.error(response.message || "Erro ao baixar arquivo");
        }
      } catch (error) {
        console.error("Erro ao baixar arquivo:", error);
        toast.error("Erro ao baixar arquivo Excel");
      }
    },

    confirmarCancelarArquivo(arquivo) {
      this.tituloConfirmacao = "Cancelar Arquivo Excel";
      this.mensagemConfirmacao = `Deseja realmente cancelar o arquivo "${arquivo.arquivo_exportado}"? As ${arquivo.quantidade_movimentacoes} Movimentacoes poderOo ser exportadas novamente.`;
      this.corBotaoConfirmacao = "error";
      this.acaoCallback = async () => {
        await this.cancelarArquivoExcel(arquivo.arquivo_exportado);
      };
      this.dialogConfirmacao = true;
    },

    async cancelarArquivoExcel(arquivoExportado) {
      try {
        const response = await sapMovimentacaoEstoqueService.cancelarArquivoExcel(arquivoExportado);

        if (response?.success) {
          toast.success(response.message || "Arquivo cancelado com sucesso!");
          this.movimentacoesSelecionadas = [];
          await this.fetchData();
          await this.listarArquivosExcel();
        } else {
          toast.error(response.message || "Erro ao cancelar arquivo");
        }
      } catch (error) {
        console.error("Erro ao cancelar arquivo:", error);
        toast.error("Erro ao cancelar arquivo Excel");
      }
    },

    // ===== UTILS =====
    getStatusColor(status) {
      const colors = {
        PENDENTE: "orange",
        PROCESSADA: "green",
        ERRO: "red",
      };
      return colors[status] || "grey";
    },

    formatarNumero(valor) {
      if (valor === null || valor === undefined || valor === '') return "0.00";
      return parseFloat(valor).toFixed(2);
    },

    unidadeProduto(produto) {
      return produto?.unidade_medida || produto?.InvntryUom || produto?.Unidade || produto?.unidade || "UN";
    },

    formatarNumeroComSinal(valor) {
      const n = parseFloat(valor || 0);
      const abs = Math.abs(n).toFixed(2);
      return n > 0 ? `+${abs}` : n < 0 ? `-${abs}` : "0.00";
    },

    saldoGestorItem(item) {
      return parseFloat(item.entradas_pendentes || 0) - parseFloat(item.saidas_pendentes || 0);
    },

    saldoGestorDeposito(dep) {
      return parseFloat(dep.entradas_pendentes || 0) - parseFloat(dep.saidas_pendentes || 0);
    },

    saldoCalculadoItem(item, nivel = 'deposito') {
      if (item?.saldo_projetado !== undefined && item?.saldo_projetado !== null) {
        return parseFloat(item.saldo_projetado || 0);
      }

      const base = nivel === 'localizacao'
        ? parseFloat(item?.estoque_localizacao || 0)
        : parseFloat(item?.estoque_fisico || 0);

      return base + this.saldoGestorItem(item);
    },

    saldoDepositoOpcao(deposito) {
      const codigo = deposito?.codigo_deposito || deposito?.id_deposito || deposito?.deposito;
      if (!codigo) return 0;
      const saldo = this.estoquesPorDepositoAgrupado.find(d => this.isSameDep(d.codigo_deposito, codigo));
      return saldo ? this.saldoCalculadoItem(saldo, 'deposito') : 0;
    },

    isSomenteGestor(item, nivel = 'deposito') {
      const sap = nivel === 'localizacao'
        ? parseFloat(item?.estoque_localizacao || 0)
        : parseFloat(item?.estoque_total ?? item?.estoque_fisico ?? 0);
      return sap <= 0 && this.saldoGestorItem(item) !== 0;
    },

    estoquesDetalhadosVisiveis(dep) {
      return this.estoquesDetalhados.filter(l =>
        this.isSameDep(l.codigo_deposito, dep.codigo_deposito) &&
        (
          parseFloat(l.estoque_localizacao || 0) > 0 ||
          parseFloat(l.saldo_projetado || 0) > 0 ||
          this.saldoGestorItem(l) !== 0
        )
      );
    },

    saldoEndereco(endereco) {
      return parseFloat(
        endereco?.saldo_projetado ??
        endereco?.estoque_localizacao ??
        endereco?.quantidade_sap ??
        endereco?.estoque_fisico ??
        endereco?.estoque_disponivel ??
        endereco?.saldo_localizacao ??
        0
      );
    },

    saldoRua(rua) {
      return this.enderecosBaseParaSelecao
        .filter(e => e.rua === rua)
        .reduce((acc, e) => acc + this.saldoEndereco(e), 0);
    },

    saldoPrateleira(rua, prateleira) {
      return this.enderecosBaseParaSelecao
        .filter(e => e.rua === rua && e.prateleira === prateleira)
        .reduce((acc, e) => acc + this.saldoEndereco(e), 0);
    },

    validarRua(valor) {
      if (!this.form.deposito || !this.localizacaoObrigatoria) return true;
      if (!valor) return 'Campo obrigatorio';

      const temPrateleira = this.enderecosBaseParaSelecao.some(e => e.rua === valor && !!e.prateleira);
      return temPrateleira || 'Esta rua nao possui prateleira no SAP';
    },

    validarPrateleira(valor) {
      if (!this.form.deposito || !this.localizacaoObrigatoria) return true;
      if (!this.form.rua) return true;
      if (!valor) return 'Campo obrigatorio';

      const existeNoSap = this.enderecosBaseParaSelecao.some(e =>
        e.rua === this.form.rua &&
        e.prateleira === valor &&
        !!e.codigo_localizacao
      );
      return existeNoSap || 'Prateleira sem Bin Code valido no SAP';
    },

    validarLocalizacao(valor) {
      return !this.localizacaoObrigatoria || !!valor || 'Campo obrigatorio';
    },

    validarQuantidadeMovimentacao(valor) {
      if (this.tipoMovimentacao !== 'SAIDA') return true;
      if (!this.produtoSelecionado) return true;

      if (!this.form.posicao) {
        const saldoBase = this.form.deposito
          ? parseFloat(this.saldoNoDepositoSelecionado || 0)
          : parseFloat(this.totalEstoqueGeral || 0);
        return valor <= saldoBase || `Qtd > Disponivel (${this.formatarNumero(saldoBase)})`;
      }

      const saldoPosicao = parseFloat(this.saldoEnderecoSelecionado || 0);
      return valor <= saldoPosicao || `Qtd > Saldo do endereco (${this.formatarNumero(saldoPosicao)})`;
    },

    sapReservadoItem(item) {
      return parseFloat(item.estoque_comprometido || item.comprometido || item.sap_reservado || 0);
    },

    sapDisponivelItem(item) {
      const fisico = parseFloat(item.estoque_localizacao || item.quantidade_sap || item.estoque_fisico || item.estoque_disponivel || 0);
      const reservado = this.sapReservadoItem(item);
      const disponivel = parseFloat(item.estoque_disponivel || item.disponivel || (fisico - reservado) || 0);
      return disponivel;
    },

    formatarDataHora(data) {
      if (!data) return "-";
      const date = new Date(data);
      return date.toLocaleString("pt-BR");
    },

    getReferenciaMovimentacao(movimentacao) {
      if (!movimentacao) return "-";
      const id = String(movimentacao.id || "").padStart(6, "0");
      const data = movimentacao.created_at ? new Date(movimentacao.created_at) : null;
      const ano = data && !Number.isNaN(data.getTime()) ? data.getFullYear() : new Date().getFullYear();
      return `MOV-${ano}-${id}`;
    },

    isSameDep(id1, id2) {
      if (!id1 || !id2) return false;
      const s1 = String(id1).trim();
      const s2 = String(id2).trim();
      return s1 === s2 || s1.padStart(2, '0') === s2.padStart(2, '0') || parseInt(s1) === parseInt(s2);
    },

    getNomeDeposito(idDeposito) {
      if (!idDeposito) return '';
      const deposito = this.depositos.find(d => this.isSameDep(d.id_deposito, idDeposito));
      return deposito ? deposito.nome_deposito : '';
    },

    voltarMenu() {
      this.$router.push("/menu");
    },

    abrirPainelSapDownloads() {
      this.$router.push({ name: 'SapDownloads' });
    },
  },

  created() {
    // Ler tipo da query param
    const tipo = this.$route.query.tipo;
    if (tipo === 'ENTRADA' || tipo === 'SAIDA') {
      this.tipoMovimentacao = tipo;
    }

    this.fetchData();
    this.carregarDepositos();
    this.carregarEnderecos();
  },
};
</script>

<style scoped>
.movimentacoes-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  background: white;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.filters-card {
  border-radius: 12px;
  overflow: hidden;
}

.filters-header {
  background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
  color: white;
}

.filters-title {
  font-weight: 600;
  font-size: 1rem;
}

.table-card {
  border-radius: 12px;
  overflow: hidden;
}

.scrollable-table {
  border-radius: 12px;
}

.movimentacoes-grid-table :deep(.v-table__wrapper) {
  overflow-x: auto !important;
}

.movimentacoes-grid-table :deep(table) {
  min-width: 1560px;
}

.movimentacoes-grid-table :deep(th),
.movimentacoes-grid-table :deep(td) {
  white-space: nowrap;
}

.descricao-chip {
  max-width: 340px;
}

.descricao-chip-text {
  display: inline-block;
  max-width: 315px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.quantidade-chip {
  min-width: 78px;
  justify-content: flex-end;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}

.cursor-pointer {
  cursor: pointer;
}

.custom-panels .v-expansion-panel-title {
  min-height: 48px !important;
  background-color: white !important;
  transition: all 0.2s ease;
}

.custom-panels .v-expansion-panel-title--active {
  background-color: #f1f8e9 !important;
}

.custom-panels .v-expansion-panel-text__wrapper {
  padding: 0 !important;
}

.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.border-e {
  border-right: 1px solid rgba(0, 0, 0, 0.1);
}

.opacity-20 {
  opacity: 0.2;
}

.opacity-40 {
  opacity: 0.4;
}

.opacity-50 {
  opacity: 0.5;
}

.premium-dialog .v-overlay__content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.observacao-scroll :deep(textarea) {
  height: 160px !important;
  min-height: 160px !important;
  max-height: 160px !important;
  overflow-y: auto !important;
  resize: none;
}

.panorama-card {
  min-height: 500px !important;
  max-height: 600px;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.produto-panorama-title {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
}

.produto-panorama-codigo {
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  color: #1f2937;
}

.produto-panorama-nome {
  font-size: 1.05rem;
  font-weight: 700;
  color: #4b5563;
}

.empresa-saldo-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background: #f9fafb;
}

.empresa-saldo-title {
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  color: #374151;
  text-transform: uppercase;
}

.empresa-saldo-item {
  font-size: 0.84rem;
  font-weight: 800;
  color: #4b5563;
}

.dep-title-name {
  font-size: 1rem;
  font-weight: 800;
}

.dep-cards-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  background: rgba(255, 255, 255, 0.6);
}

.dep-summary-item {
  min-width: 94px;
  text-align: right;
  border-radius: 10px;
  padding: 3px 7px 4px;
  border: 1px solid transparent;
}

.dep-summary-label {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 1;
  margin-bottom: 2px;
}

.dep-summary-value {
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.1;
}

.dep-summary-item--sap {
  background: #f5f7fa;
  border-color: #dfe4ec;
  color: #455a64;
}

.dep-summary-item--sap .dep-summary-label {
  color: #607d8b;
}

.dep-summary-item--gestor {
  background: #fff7ed;
  border-color: #fed7aa;
}

.dep-summary-item--gestor .dep-summary-label {
  color: #ea580c;
}

.dep-summary-item--geral {
  background: #eff6ff;
  border-color: #bfdbfe;
  color: #0d47a1;
}

.dep-summary-item--geral .dep-summary-label {
  color: #1565c0;
}

.stock-line-values {
  gap: 10px;
}

.stock-col {
  min-width: 78px;
}

.stock-col-label {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1;
  margin-bottom: 2px;
}

.has-help {
  cursor: help;
}


.detalhes-dialog-card {
  max-height: 88vh;
  border-radius: 12px;
}

.detalhes-dialog-body {
  max-height: calc(88vh - 132px);
  overflow-y: auto;
}

.detalhes-ref-tag {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.36);
}

.v-dialog.premium-dialog>.v-overlay__content>.v-card {
  min-height: 650px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
